import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getRouteCreateProject,
  getRouteNewVersion,
  getRouteProjectDetails,
  getRouteProjects,
  getRouteReviewRelaunch
} from "@/zapi-frontend/shared/const/router";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { MemoIsland } from "./MemoIsland/MemoIsland";
import { ProjectSidebar } from "./ProjectSidebar/ProjectSidebar";
import { WorkspaceRoomsIsland } from "./Repository/WorkspaceRoomsIsland";
import { AnalysisIsland } from "./AnalysisIsland";
import { ProjectHeader } from "./ProjectHeader/ProjectHeader";
import { ProjectLoadingScreen } from "./ProjectLoadingScreen";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { isChapterGenerating, useChatStore } from "@/zapi-frontend/stores/chatStore";
import { InstructionsIsland } from "./InstructionsIsland";
import { InstructionsHeader } from "./InstructionsHeader";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { useProjectSetupPolling } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { useMarkChapterNotificationAsRead, useNotificationStore } from "@/zapi-frontend/features/Notifications";
import { useBackRedirectFromProjectList } from "@/zapi-frontend/shared/hooks/useBackToProjectOverride";
import { SS_BACK_TO_PROJECT_PRESENTATION } from "@/zapi-frontend/shared/session-storage/keys";
import { DashboardIsland } from "./DashboardIsland/DashboardIsland";
import { ProjectOverviewIsland } from "./ProjectOverviewIsland";
import { CaseFrameIsland } from "./FrameIsland/CaseFrameIsland";
import { CaseFrameChatIsland } from "./FrameIsland/CaseFrameChatIsland";
import { ProjectSetupIsland } from "./FrameIsland/ProjectSetupIsland";
import { VaultIsland } from "./VaultIsland/VaultIsland";
import { ExportsIsland } from "./ExportsIsland/ExportsIsland";
import { ValueChainIsland } from "./ValueChainIsland/ValueChainIsland";
import { PresentationPage } from "./PresentationPage/ui/PresentationPage";
import { projectPageBackgroundStyle } from "@/zapi-frontend/shared/lib/projectTheme";
import { useProjectThemeStore } from "@/zapi-frontend/stores/projectTheme";
import { useProjectLanguageSync } from "@/zapi-frontend/shared/i18n/useProjectLanguageSync";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import { SettingUpAnalysisScreen } from "./GenerationStates/SettingUpAnalysisScreen";
import { ChapterQueuedScreen } from "./GenerationStates/ChapterQueuedScreen";
import { ChapterGeneratingSkeleton } from "./GenerationStates/ChapterGeneratingSkeleton";

export function ProjectDetailsPage() {
  const { id } = useParams();
  return <ProjectDetailsPageContent key={typeof id === "string" ? id : ""} />;
}

function ProjectDetailsPageContent() {
  const { id } = useParams();
  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchSelectedProject, isFetchProjectLoading, setSelectedProject, selectedProject } = useProjectStore();
  const { initializeProjectChapters, viewMode, setViewMode, activeChapter, setActiveChapter, projectChapters } =
    useChapterStore();
  const { navigate } = useHistoricalNavigate();
  const { closeHandler, openSidebar } = useProjectSidebarStore();
  const previewIconColor = useProjectThemeStore((s) => s.previewIconColor);
  const { getLoadingState } = useChatStore();
  useMarkChapterNotificationAsRead();
  useProjectLanguageSync(selectedProject?.language);
  const [isProjectLoaded, setIsProjectLoaded] = useState(false);

  const {
    isPollingInitialized,
    chapterMemoPolling,
    chapterStatusMap,
    cancelSetup,
    isCancelling,
    canCancel
  } = useProjectSetupPolling(
      projectId,
      activeChapter?.id ?? null,
      isProjectLoaded,
      useCallback(() => {
        fetchSelectedProject(projectId, true);
        setIsProjectLoaded(true);
      }, [projectId, fetchSelectedProject])
    );

  const chapterMemoStatus = chapterMemoPolling.chapters.find((i) => i.id === activeChapter?.id);
  const isActiveChapterHasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? !!activeChapter?.activeMemo;

  const handleCancelSetup = useCallback(async () => {
    const result = await cancelSetup();
    if (!result) return;

    useNotificationStore.getState().removeProjectNotifications(Number(projectId));

    if (result.isRelaunchBrief) {
      navigate(getRouteReviewRelaunch(projectId));
    } else if (selectedProject?.versionGroupId) {
      navigate(getRouteNewVersion(projectId));
    } else {
      navigate(`${getRouteCreateProject()}?caseBriefId=${result.caseBriefId}`);
    }
  }, [cancelSetup, navigate, projectId, selectedProject?.versionGroupId]);

  const loadProject = useCallback(async () => {
    const [response, error] = await tryCatch(fetchSelectedProject(projectId));

    if (error) {
      toast.error("Failed to load project");
      navigate(getRouteProjects());
      return;
    }

    if (response) {
      initializeProjectChapters(response);
      setIsProjectLoaded(true);
    }
  }, [projectId, fetchSelectedProject, initializeProjectChapters, navigate]);

  useEffect(() => {
    if (!projectId) return;
    setIsProjectLoaded(false);
    loadProject();
  }, [projectId, loadProject]);

  useEffect(() => {
    const hasChapterId = searchParams.has("chapterId");
    const viewParam = searchParams.get("view");

    if (viewParam === "dashboard" && viewMode !== "dashboard") {
      setViewMode("dashboard");
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("view");
          return params;
        },
        { replace: true }
      );
    } else if (viewParam === "instructions") {
      setViewMode("instructions");
    } else if (
      viewParam === "projectFrame" ||
      viewParam === "projectConfig" ||
      viewParam === "vault" ||
      viewParam === "exports" ||
      viewParam === "valueChain"
    ) {
      if (viewMode !== viewParam) setViewMode(viewParam);
    } else if (hasChapterId) {
      const chapterIdParam = searchParams.get("chapterId");
      if (
        chapterIdParam &&
        chapterIdParam !== activeChapter?.id?.toString() &&
        projectChapters.some((c) => c.id === Number(chapterIdParam))
      ) {
        setActiveChapter(chapterIdParam);
      }
      if (viewMode !== "chapter") {
        setViewMode("chapter");
      }
    } else if (projectId) {
      // Home is the only fallback view now, so it can be picked before the project
      // loads — waiting left the header on "Loading project..." with chapter tools.
      if (viewMode === "instructions" || (!viewParam && viewMode !== "dashboard")) {
        setViewMode("projectOverview");
      }
    }
  }, [
    searchParams,
    viewMode,
    setViewMode,
    setActiveChapter,
    activeChapter?.id,
    setSearchParams,
    projectId,
    isProjectLoaded,
    projectChapters,
    isPollingInitialized,
    canCancel
  ]);

  useEffect(() => {
    return () => {
      setViewMode(undefined);
      setSelectedProject(null);
      closeHandler();
      useProjectThemeStore.getState().setPreviewIconColor(null);
    };
  }, [setViewMode, setSelectedProject, closeHandler]);

  useEffect(() => {
    if (isFetchProjectLoading || !isActiveChapterHasActiveMemo) closeHandler();
  }, [isFetchProjectLoading, isActiveChapterHasActiveMemo, closeHandler]);
  // Until check-active (or a stored task) answers we do not know whether the project is
  // generating; rendering real content before that flashes it for a few seconds.
  const isGenerationResolved = isPollingInitialized || canCancel;
  const isViewModeResolved = searchParams.has("chapterId") || isGenerationResolved;

  const generationState = useProjectGenerationState(projectId);
  const isDisabledDueToProjectSetup =
    (generationState.isSetupActive && !isActiveChapterHasActiveMemo) || !!generationState.debugState;
  const queuedChapterPills = activeChapter ? generationState.getQueuedPills(activeChapter) : [];
  const generationProgress = generationState.isSetupActive
    ? {
        projectId,
        onCancel: generationState.debugState
          ? () => toast.info("debugGenState is on — cancel is stubbed")
          : canCancel
            ? handleCancelSetup
            : undefined,
        isCancelling
      }
    : undefined;

  const activeChapterId = activeChapter?.id?.toString() ?? "";
  const isChatLoading = isChapterGenerating(getLoadingState(projectId, activeChapterId));

  const isPresentationTab = searchParams.get("tab") === "presentation";
  const isChromelessView =
    viewMode === "projectOverview" ||
    viewMode === "projectFrame" ||
    viewMode === "projectConfig" ||
    viewMode === "vault" ||
    viewMode === "exports";
  const isFrameOrArtifactsView =
    viewMode === "projectFrame" ||
    viewMode === "projectConfig" ||
    viewMode === "vault" ||
    viewMode === "valueChain" ||
    viewMode === "exports";
  const showPageLoader = isFrameOrArtifactsView
    ? isFetchProjectLoading
    : ((isFetchProjectLoading ||
        !isViewModeResolved ||
        (viewMode === "instructions" && isDisabledDueToProjectSetup)) &&
        viewMode !== "dashboard" &&
        viewMode !== "projectOverview");
  // Generation screens keep the page chrome (title + divider) and a white content panel,
  // per design — unlike the finished Home, which is chromeless at `hd`. The Home skeleton
  // is the exception: it reuses the overview's cards-on-gray layout.
  const isHomeGenerationView = viewMode === "projectOverview" && generationState.isSetupActive;
  const showHomeGenerationChrome = isHomeGenerationView;
  const showHomeGenerationPanel = isHomeGenerationView && !generationState.isHomeGenerating;
  const showChapterGenerationState = !showPageLoader && viewMode === "chapter" && isDisabledDueToProjectSetup;
  const showChapterChatSkeleton =
    !showPageLoader &&
    !showChapterGenerationState &&
    viewMode === "chapter" &&
    isChatLoading &&
    !isActiveChapterHasActiveMemo;

  useBackRedirectFromProjectList({
    projectId,
    isActive: isPresentationTab,
    storageKey: SS_BACK_TO_PROJECT_PRESENTATION,
    returnUrl: `${getRouteProjectDetails(projectId)}?tab=document`
  });

  if (isPresentationTab) return <PresentationPage />;

  return (
    <div
      className={cn(
        "flex bg-projectPage gap-2 h-[calc(100svh-56px)] w-full p-2 overflow-hidden relative"
      )}
      style={projectPageBackgroundStyle(previewIconColor ?? selectedProject?.iconColor)}
    >
      <ProjectSidebar />
      <div
        className={cn(
          "flex flex-col flex-1 min-w-0 h-full",
          isChromelessView && !showHomeGenerationPanel ? "bg-projectPage" : "bg-white"
        )}
      >
        {viewMode === "instructions" && <InstructionsHeader />}
        {viewMode !== "instructions" && (
          <div className={cn(isChromelessView && !showHomeGenerationChrome && "hd:hidden")}>
            <ProjectHeader
              chapterMemoPolling={chapterMemoPolling}
              hideTitle={isChromelessView && !showHomeGenerationChrome}
            />
          </div>
        )}
        <div
          className={cn(
            "grid w-full grid-cols-[1fr_0px] grid-rows-[minmax(0,1fr)] flex-1 min-h-0 transition-all duration-300",
            activeChapter?.projectTemplateChapter.isExecutiveSummary && "max-h-[calc(100svh-270px)]",
            !!openSidebar && "grid-cols-[0px_1fr] tablet:grid-cols-[1fr_312px]"
          )}
        >
          {showPageLoader ? (
            <div className="w-full min-w-0 h-full min-h-0 bg-white flex flex-col justify-center items-center gap-4 overflow-y-auto shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)]">
              <ProjectLoadingScreen />
            </div>
          ) : showChapterGenerationState ? (
            <div className="w-full min-w-0 h-full min-h-0 bg-white overflow-y-auto shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)]">
              {generationState.isGenerationDataPending ? (
                <div className="w-full h-full flex items-center justify-center">
                  <ProjectLoadingScreen />
                </div>
              ) : generationState.isSettingUp ? (
                // The card floats above the page-level dim, so the panel behind stays empty.
                <div className="w-full h-full" />
              ) : activeChapter && generationState.getChapterState(activeChapter) === "generating" ? (
                <ChapterGeneratingSkeleton />
              ) : (
                <ChapterQueuedScreen
                  title={activeChapter?.projectTemplateChapter.name ?? "Chapter"}
                  pills={queuedChapterPills}
                />
              )}
            </div>
          ) : showChapterChatSkeleton ? (
            <div className="w-full min-w-0 h-full min-h-0 bg-white overflow-y-auto shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)]">
              <ChapterGeneratingSkeleton />
            </div>
          ) : (
            <>
              {viewMode === "dashboard" && (
                <DashboardIsland
                  chapterStatusMap={chapterStatusMap}
                  chapterMemoPolling={chapterMemoPolling}
                  onCancelSetup={canCancel ? handleCancelSetup : undefined}
                  isCancelling={isCancelling}
                />
              )}
              {viewMode === "projectOverview" && <ProjectOverviewIsland isGenerationResolved={isGenerationResolved} />}
              {viewMode === "projectFrame" && <CaseFrameIsland />}
              {viewMode === "projectConfig" && <ProjectSetupIsland />}
              {viewMode === "vault" && isProjectLoaded && <VaultIsland />}
              {viewMode === "valueChain" && isProjectLoaded && <ValueChainIsland />}
              {viewMode === "exports" && isProjectLoaded && <ExportsIsland />}
              {(viewMode === "chapter" || viewMode === "instructions") && (
                <div className="w-full flex">
                  {viewMode === "instructions" && <InstructionsIsland />}
                  {viewMode !== "instructions" && (
                    <div className="w-full laptop:px-7">
                      <MemoIsland />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <AnalysisIsland projectId={projectId} isChapterContentReady={isActiveChapterHasActiveMemo} />
        </div>
      </div>
      {viewMode === "chapter" && <WorkspaceRoomsIsland generationProgress={generationProgress} />}
      {viewMode === "projectFrame" &&
        !generationState.isSetupActive &&
        (isProjectLoaded ? (
          <CaseFrameChatIsland />
        ) : (
          <WorkspaceRoomsIsland askOnly generationProgress={generationProgress} />
        ))}
      {((viewMode === "projectFrame" && generationState.isSetupActive) ||
        viewMode === "projectOverview" ||
        viewMode === "projectConfig" ||
        viewMode === "vault" ||
        viewMode === "valueChain" ||
        viewMode === "exports") && (
          <WorkspaceRoomsIsland
            chapter={
              projectChapters.find((c) => c.projectTemplateChapter.slug === "executive-summary-10-pager") ??
              projectChapters.find(
                (c) =>
                  c.projectTemplateChapter.isExecutiveSummary ||
                  c.projectTemplateChapter.slug.startsWith("executive-summary")
              ) ??
              projectChapters[0]
            }
            askOnly
            generationProgress={generationProgress}
          />
        )}
      {generationState.isSettingUp && !generationState.isGenerationDataPending && (
        // Per design the app is dimmed behind the card, so the page (sidebar, header,
        // content panel) stays visible instead of being replaced by a blank screen.
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/10 pointer-events-none">
          <div className="pointer-events-auto">
            <SettingUpAnalysisScreen />
          </div>
        </div>
      )}
    </div>
  );
}
